import { ArrowDown, ArrowUp, ArrowUpDown, Copy, Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { authFetch } from '../auth'
import { MetricStrip } from '../components/MetricStrip'
import {
  campaignCpsCommission,
  loadCampaigns,
  resolvedCampaignItemCpsPercent,
  saveCampaigns,
  type Campaign,
  type CampaignAnalysisData,
  type CampaignLayerMethod,
} from '../campaigns'
import { useProductData } from '../data'
import { loadListingTimes, type ListingTimesData } from '../listingTimes'
import {
  accessoryFor,
  internalProductCostOn,
  JD_STORE_ID,
  productMasterCostDefaults,
  productMasterRecordMap,
  rebateRateFor,
  resolvedProductCost,
  shippingProviderFor,
  unitOperatingCost,
} from '../productMaster'
import { internalProductRecordsByCode, summarizeProductProfit } from '../productProfit'
import { skuMatchesTag } from '../segments'
import type { ShippingProvider } from '../types'
import { integer, metricsAcrossDates, money, percent } from '../utils'

const shippingProviderLabel = (provider: ShippingProvider) =>
  provider === 'jd-warehouse' ? '京东入仓' : provider === 'yto-express' ? '圆通快递' : '京东快递'

const LAYER_METHOD_LABELS: Record<CampaignLayerMethod, string> = {
  amount: '直降',
  percent: '折扣',
  threshold: '每满减',
}

interface DraftLayer {
  key: string
  name: string
  method: CampaignLayerMethod
  value: string
  threshold: string
  platformShare: string
}

interface DraftItem {
  jdSkuId: string
  basePrice: string
  cpsCommissionPercent: string
}

interface CampaignDraft {
  id: string
  name: string
  startDate: string
  endDate: string
  note: string
  cpsCommissionPercent: string
  layers: DraftLayer[]
  items: DraftItem[]
}

const newKey = (prefix: string) => `${prefix}-${Date.now().toString(16)}${Math.random().toString(16).slice(2, 8)}`

const emptyLayer = (): DraftLayer => ({
  key: newKey('layer'),
  name: '',
  method: 'percent',
  value: '',
  threshold: '',
  platformShare: '',
})

const emptyDraft = (): CampaignDraft => ({
  id: newKey('campaign'),
  name: '',
  startDate: '',
  endDate: '',
  note: '',
  cpsCommissionPercent: '',
  layers: [],
  items: [],
})

const draftFromCampaign = (campaign: Campaign, withNewId = false): CampaignDraft => ({
  id: withNewId ? newKey('campaign') : campaign.id,
  name: withNewId ? `${campaign.name} 副本` : campaign.name,
  startDate: campaign.startDate,
  endDate: campaign.endDate,
  note: campaign.note,
  cpsCommissionPercent: campaign.cpsCommissionPercent ? String(campaign.cpsCommissionPercent) : '',
  layers: (campaign.layers || []).map((layer) => ({
    key: layer.id || newKey('layer'),
    name: layer.name,
    method: layer.method,
    value: String(layer.value),
    threshold: layer.threshold === null || layer.threshold === undefined ? '' : String(layer.threshold),
    platformShare: layer.platformSharePercent ? String(layer.platformSharePercent) : '',
  })),
  items: (campaign.items || []).map((item) => ({
    jdSkuId: item.jdSkuId,
    basePrice: item.basePrice === null || item.basePrice === undefined ? '' : String(item.basePrice),
    cpsCommissionPercent: item.cpsCommissionPercent === null || item.cpsCommissionPercent === undefined
      ? ''
      : String(item.cpsCommissionPercent),
  })),
})

const campaignFromDraft = (draft: CampaignDraft): Campaign => ({
  id: draft.id,
  name: draft.name.trim(),
  startDate: draft.startDate,
  endDate: draft.endDate,
  note: draft.note.trim(),
  cpsCommissionPercent: draft.cpsCommissionPercent.trim() === '' ? 0 : Number(draft.cpsCommissionPercent),
  layers: draft.layers.map((layer, index) => ({
    id: layer.key,
    name: layer.name.trim() || `优惠层${index + 1}`,
    method: layer.method,
    value: Number(layer.value),
    threshold: layer.method === 'threshold' ? Number(layer.threshold) : null,
    platformSharePercent: layer.platformShare.trim() === '' ? 0 : Number(layer.platformShare),
  })),
  items: draft.items.map((item) => ({
    jdSkuId: item.jdSkuId,
    basePrice: item.basePrice.trim() === '' ? null : Number(item.basePrice),
    cpsCommissionPercent: item.cpsCommissionPercent.trim() === '' ? null : Number(item.cpsCommissionPercent),
  })),
})

interface ParsedLayer {
  name: string
  method: CampaignLayerMethod
  value: number
  threshold: number | null
  share: number
  valid: boolean
}

const parseLayer = (layer: DraftLayer, index: number): ParsedLayer => {
  const value = Number(layer.value)
  const threshold = layer.method === 'threshold' ? Number(layer.threshold) : null
  const share = layer.platformShare.trim() === '' ? 0 : Number(layer.platformShare)
  const valueValid = layer.value.trim() !== '' && Number.isFinite(value) && value > 0
    && (layer.method !== 'percent' || value <= 10)
  const thresholdValid = layer.method !== 'threshold'
    || (layer.threshold.trim() !== '' && Number.isFinite(threshold as number) && (threshold as number) > 0 && value < (threshold as number))
  const shareValid = Number.isFinite(share) && share >= 0 && share <= 100
  return {
    name: layer.name.trim() || `优惠层${index + 1}`,
    method: layer.method,
    value,
    threshold,
    share,
    valid: valueValid && thresholdValid && shareValid,
  }
}

const layerSummary = (layer: ParsedLayer) => {
  if (!layer.valid) return '待完善'
  if (layer.method === 'amount') return `直降 ${money(layer.value)}`
  if (layer.method === 'percent') return `到手 × ${layer.value}折`
  const rate = layer.value / (layer.threshold || 1) * 100
  return `折算让利 ${rate.toFixed(2)}%（按凑满门槛）`
}

export function CampaignAnalysisPage() {
  const { data, selectedDate, productIndex, segmentIndex, productMaster, internalProductInfo } = useProductData()
  const [campaignsData, setCampaignsData] = useState<CampaignAnalysisData | null | undefined>(undefined)
  const [listing, setListing] = useState<ListingTimesData | null | undefined>(undefined)
  const [draft, setDraft] = useState<CampaignDraft | null>(null)
  const [batchText, setBatchText] = useState('')
  const [batchReport, setBatchReport] = useState('')
  const [sortRule, setSortRule] = useState<{ key: 'profit' | 'profitWithRebate' | 'margin', direction: 'asc' | 'desc' } | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadCampaigns().then((value) => { if (!cancelled) setCampaignsData(value) })
    loadListingTimes().then((value) => { if (!cancelled) setListing(value) })
    return () => { cancelled = true }
  }, [])

  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const companyRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )
  const jdPriceBySku = useMemo(
    () => new Map((listing?.records || []).map((record) => [record.jdSkuId, record.jdPrice ?? null])),
    [listing],
  )
  const merchantSkuIndex = useMemo(() => {
    const index = new Map<string, string[]>()
    for (const product of data?.products || []) {
      if (product.level !== 'sku' || !product.merchantSku) continue
      const key = product.merchantSku.trim()
      index.set(key, [...(index.get(key) || []), product.id])
    }
    return index
  }, [data])

  const selectCampaign = (campaign: Campaign | null) => {
    setDraft(campaign ? draftFromCampaign(campaign) : null)
    setBatchText('')
    setBatchReport('')
    setMessage('')
    setError('')
    setPendingDelete(false)
  }

  const parsedLayers = useMemo(
    () => (draft?.layers || []).map((layer, index) => parseLayer(layer, index)),
    [draft],
  )
  const activeLayers = useMemo(() => parsedLayers.filter((layer) => layer.valid), [parsedLayers])
  const cpsCommissionPercent = draft?.cpsCommissionPercent.trim() === ''
    ? 0
    : Number(draft?.cpsCommissionPercent)
  const cpsCommissionPercentValid = Number.isFinite(cpsCommissionPercent)
    && cpsCommissionPercent >= 0
    && cpsCommissionPercent <= 100

  const rows = useMemo(() => {
    if (!data || !draft) return []
    const dates = data.meta.dates
    return draft.items.map((item, index) => {
      const product = productIndex.get(`sku:${item.jdSkuId}`)
      const record = productMasterRecords.get(item.jdSkuId)
      const companyRecords = product ? companyRecordsByCode.get(product.merchantSku.trim()) || [] : []
      const internalCost = internalProductCostOn(
        companyRecords,
        record,
        draft.startDate || selectedDate,
      )
      const isAccessory = product ? accessoryFor(product, record, companyRecords) : false
      const inheritedJdWarehouse = skuMatchesTag(segmentIndex, item.jdSkuId, 'jd-warehouse', dates)
      const shippingProvider = shippingProviderFor(
        record,
        inheritedJdWarehouse,
        selectedDate,
        costDefaults,
        isAccessory,
        internalCost.isBrandSpecial,
      )
      const giftExcluded = isAccessory || internalCost.isBrandSpecial
      const cost = resolvedProductCost(record, selectedDate, costDefaults, shippingProvider, giftExcluded)
      const unitCost = internalCost.productCost === null ? null : unitOperatingCost(cost, internalCost.productCost)
      const rateSum = cost.platformFeeRate + cost.taxRate
      const jdPrice = jdPriceBySku.get(item.jdSkuId) ?? null
      const manualBase = item.basePrice.trim() === '' ? null : Number(item.basePrice)
      const manualValid = manualBase === null || (Number.isFinite(manualBase) && manualBase >= 0)
      const basePrice = manualValid && manualBase !== null ? manualBase : jdPrice
      const itemCpsCommissionPercent = item.cpsCommissionPercent.trim() === ''
        ? null
        : Number(item.cpsCommissionPercent)
      const itemCpsCommissionPercentValid = itemCpsCommissionPercent === null
        || (Number.isFinite(itemCpsCommissionPercent)
          && itemCpsCommissionPercent >= 0
          && itemCpsCommissionPercent <= 100)
      const effectiveCpsCommissionPercent = resolvedCampaignItemCpsPercent(
        itemCpsCommissionPercentValid ? itemCpsCommissionPercent : null,
        cpsCommissionPercent,
      )
      let finalPrice: number | null = null
      let subsidy = 0
      const steps: string[] = []
      if (basePrice !== null) {
        if (jdPrice !== null && basePrice < jdPrice) {
          steps.push(`底价直降 ${money(jdPrice - basePrice)}（商家承担）`)
        }
        let price = basePrice
        for (const layer of activeLayers) {
          let next = price
          if (layer.method === 'percent') next = price * layer.value / 10
          else if (layer.method === 'amount') next = Math.max(0, price - layer.value)
          else next = price * (1 - layer.value / (layer.threshold || 1))
          const concession = price - next
          const subsidyPart = concession * layer.share / 100
          subsidy += subsidyPart
          steps.push(`${layer.name} 让利 ${money(concession)}${subsidyPart > 0 ? `，平台补 ${money(subsidyPart)}` : ''}`)
          price = next
        }
        finalPrice = price
      }
      const merchantReceives = finalPrice === null ? null : finalPrice + subsidy
      const cpsCommission = campaignCpsCommission(finalPrice, effectiveCpsCommissionPercent)
      const profit = merchantReceives !== null && unitCost !== null && cpsCommission !== null
        ? merchantReceives - unitCost - merchantReceives * rateSum / 100 - cpsCommission
        : null
      const rebateRate = rebateRateFor(isAccessory, internalCost.isBrandSpecial, internalCost.productCost)
      const rebateUnit = rebateRate !== null && internalCost.productCost !== null
        ? internalCost.productCost * rebateRate
        : null
      const profitWithRebate = profit === null ? null : profit + (rebateUnit || 0)
      return {
        item,
        index,
        product,
        internalCost,
        isAccessory,
        cost,
        unitCost,
        rateSum,
        jdPrice,
        manualValid,
        basePrice,
        itemCpsCommissionPercentValid,
        effectiveCpsCommissionPercent,
        finalPrice,
        subsidy,
        steps,
        merchantReceives,
        cpsCommission,
        profit,
        rebateUnit,
        rebateRate,
        profitWithRebate,
        margin: profitWithRebate !== null && merchantReceives !== null && merchantReceives > 0
          ? profitWithRebate / merchantReceives
          : null,
        priceRatio: finalPrice !== null && jdPrice !== null && jdPrice > 0 ? finalPrice / jdPrice : null,
      }
    })
  }, [activeLayers, companyRecordsByCode, costDefaults, cpsCommissionPercent, data, draft, jdPriceBySku, productIndex, productMasterRecords, segmentIndex, selectedDate])

  const sortedRows = useMemo(() => {
    if (!sortRule) return rows
    const next = [...rows]
    next.sort((a, b) => {
      const left = a[sortRule.key]
      const right = b[sortRule.key]
      if (left === null && right === null) return 0
      if (left === null) return 1
      if (right === null) return -1
      return sortRule.direction === 'asc' ? left - right : right - left
    })
    return next
  }, [rows, sortRule])

  const toggleSort = (key: 'profit' | 'profitWithRebate' | 'margin') => {
    setSortRule((current) => {
      if (!current || current.key !== key) return { key, direction: 'desc' }
      if (current.direction === 'desc') return { key, direction: 'asc' }
      return null
    })
  }

  const renderSortableHeader = (key: 'profit' | 'profitWithRebate' | 'margin', label: string) => {
    const active = sortRule?.key === key
    return (
      <th aria-sort={active ? (sortRule.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
        <button
          type="button"
          className={`campaign-sort-button${active ? ' is-active' : ''}`}
          title="点击排序：先降序，再升序，第三次恢复输入顺序"
          onClick={() => toggleSort(key)}
        >
          {label}
          {active
            ? sortRule.direction === 'desc' ? <ArrowDown aria-hidden="true" /> : <ArrowUp aria-hidden="true" />
            : <ArrowUpDown aria-hidden="true" />}
        </button>
      </th>
    )
  }

  const summary = useMemo(() => {
    const computable = rows.filter((row) => row.profitWithRebate !== null)
    const losing = computable.filter((row) => (row.profitWithRebate || 0) < 0)
    const margins = computable.filter((row) => row.margin !== null)
    return {
      total: rows.length,
      computable: computable.length,
      losing: losing.length,
      averageMargin: margins.length
        ? margins.reduce((sum, row) => sum + (row.margin || 0), 0) / margins.length
        : null,
    }
  }, [rows])

  const reviewWindow = useMemo(() => {
    if (!data || !draft || !draft.startDate) return null
    const allDates = data.meta.dates
    const endDate = draft.endDate || allDates[allDates.length - 1]
    const dates = allDates.filter((date) => date >= draft.startDate && date <= endDate)
    if (!dates.length) return null
    const requestedDays = Math.floor((Date.parse(endDate) - Date.parse(draft.startDate)) / 86400000) + 1
    return { dates, start: dates[0], end: dates[dates.length - 1], requestedDays }
  }, [data, draft])

  const review = useMemo(() => {
    if (!data || !draft || !reviewWindow) return null
    const expectedUnitBySku = new Map(rows.map((row) => [row.item.jdSkuId, row.profitWithRebate]))
    const cpsCommissionPercentBySku = new Map(rows.map((row) => [row.item.jdSkuId, row.effectiveCpsCommissionPercent]))
    const items = draft.items.map((item) => {
      const product = productIndex.get(`sku:${item.jdSkuId}`)
      if (!product) return null
      const actual = metricsAcrossDates(product, reviewWindow.dates)
      if (actual.gmv <= 0 && actual.soldItemCount <= 0) return null
      const profitSummary = summarizeProductProfit(
        [product],
        reviewWindow.dates,
        selectedDate,
        productMasterRecords,
        costDefaults,
        companyRecordsByCode,
        segmentIndex,
      )
      const expectedUnit = expectedUnitBySku.get(item.jdSkuId) ?? null
      const expected = expectedUnit !== null ? expectedUnit * actual.soldItemCount : null
      const itemCpsCommissionPercent = cpsCommissionPercentBySku.get(item.jdSkuId) ?? cpsCommissionPercent
      const actualCpsCommission = actual.gmv * itemCpsCommissionPercent / 100
      const actualProfit = profitSummary.profitWithRebate === null
        ? null
        : profitSummary.profitWithRebate - actualCpsCommission
      return {
        jdSkuId: item.jdSkuId,
        product,
        actual,
        itemCpsCommissionPercent,
        actualCpsCommission,
        actualProfit,
        expectedUnit,
        expected,
        diff: actualProfit !== null && expected !== null ? actualProfit - expected : null,
      }
    }).filter((row): row is NonNullable<typeof row> => row !== null)
    items.sort((a, b) => b.actual.gmv - a.actual.gmv)
    const covered = items.filter((row) => row.actualProfit !== null)
    return {
      items,
      totalGmv: items.reduce((sum, row) => sum + row.actual.gmv, 0),
      totalItems: items.reduce((sum, row) => sum + row.actual.soldItemCount, 0),
      totalCpsCommission: items.reduce((sum, row) => sum + row.actualCpsCommission, 0),
      actualProfit: covered.length ? covered.reduce((sum, row) => sum + (row.actualProfit || 0), 0) : null,
      expectedProfit: covered.some((row) => row.expected !== null)
        ? covered.reduce((sum, row) => sum + (row.expected || 0), 0)
        : null,
      coveredCount: covered.length,
    }
  }, [companyRecordsByCode, costDefaults, cpsCommissionPercent, data, draft, productIndex, productMasterRecords, reviewWindow, rows, segmentIndex, selectedDate])

  const updateLayer = (key: string, patch: Partial<DraftLayer>) => {
    if (!draft) return
    setDraft({
      ...draft,
      layers: draft.layers.map((layer) => layer.key === key ? { ...layer, ...patch } : layer),
    })
  }

  const moveLayer = (key: string, direction: -1 | 1) => {
    if (!draft) return
    const index = draft.layers.findIndex((layer) => layer.key === key)
    const target = index + direction
    if (index < 0 || target < 0 || target >= draft.layers.length) return
    const layers = [...draft.layers]
    ;[layers[index], layers[target]] = [layers[target], layers[index]]
    setDraft({ ...draft, layers })
  }

  const applyBatchInput = () => {
    if (!draft) return
    const lines = Array.from(new Set(batchText.split('\n').map((line) => line.trim()).filter(Boolean)))
    if (!lines.length) return
    const existing = new Set(draft.items.map((item) => item.jdSkuId))
    const added: string[] = []
    let duplicates = 0
    const unknown: string[] = []
    for (const line of lines) {
      let skuId = ''
      if (productIndex.has(`sku:${line}`)) skuId = line
      else {
        const matches = merchantSkuIndex.get(line) || []
        if (matches.length === 1) skuId = matches[0]
        else if (matches.length > 1) { unknown.push(`${line}(商家SKU对应多个)`); continue }
      }
      if (!skuId) { unknown.push(line); continue }
      if (existing.has(skuId)) { duplicates += 1; continue }
      existing.add(skuId)
      added.push(skuId)
    }
    if (added.length) {
      setDraft({
        ...draft,
        items: [...draft.items, ...added.map((skuId) => ({ jdSkuId: skuId, basePrice: '', cpsCommissionPercent: '' }))],
      })
    }
    setBatchReport(
      `新增 ${added.length} 个` +
      (duplicates ? ` · 已在列表 ${duplicates} 个` : '') +
      (unknown.length ? ` · 未匹配 ${unknown.length} 个：${unknown.slice(0, 5).join('、')}${unknown.length > 5 ? '…' : ''}` : ''),
    )
    if (added.length) setBatchText('')
  }

  const updateItemBasePrice = (jdSkuId: string, value: string) => {
    if (!draft) return
    setDraft({
      ...draft,
      items: draft.items.map((item) => item.jdSkuId === jdSkuId ? { ...item, basePrice: value } : item),
    })
  }

  const updateItemCpsCommissionPercent = (jdSkuId: string, value: string) => {
    if (!draft) return
    setDraft({
      ...draft,
      items: draft.items.map((item) => item.jdSkuId === jdSkuId ? { ...item, cpsCommissionPercent: value } : item),
    })
  }

  const removeItem = (jdSkuId: string) => {
    if (!draft) return
    setDraft({ ...draft, items: draft.items.filter((item) => item.jdSkuId !== jdSkuId) })
  }

  const persist = async (nextCampaigns: Campaign[], successMessage: string, clearDraft = false) => {
    setSaving(true)
    setError('')
    try {
      const saved = await saveCampaigns(nextCampaigns, authFetch)
      setCampaignsData(saved)
      setMessage(successMessage)
      if (clearDraft) setDraft(null)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!draft || !campaignsData) return
    if (!draft.name.trim()) { setError('请输入活动名称'); return }
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) { setError('结束日期不能早于开始日期'); return }
    if (!cpsCommissionPercentValid) { setError('京挑客 CPS 佣金率需在0到100之间'); return }
    const badLayerIndex = parsedLayers.findIndex((layer) => !layer.valid)
    if (badLayerIndex >= 0) { setError(`第${badLayerIndex + 1}层优惠未填完整：折扣0-10折、直降金额大于0、满减需门槛大于减额，平台承担0-100`); return }
    const invalidItem = draft.items.find((item) => item.basePrice.trim() !== '' && (!Number.isFinite(Number(item.basePrice)) || Number(item.basePrice) < 0))
    if (invalidItem) { setError(`SKU ${invalidItem.jdSkuId} 的活动底价无效`); return }
    const invalidCpsItem = draft.items.find((item) => item.cpsCommissionPercent.trim() !== '' && (
      !Number.isFinite(Number(item.cpsCommissionPercent))
      || Number(item.cpsCommissionPercent) < 0
      || Number(item.cpsCommissionPercent) > 100
    ))
    if (invalidCpsItem) { setError(`SKU ${invalidCpsItem.jdSkuId} 的京挑客 CPS 佣金率需在0到100之间`); return }
    const campaign = campaignFromDraft(draft)
    const others = campaignsData.campaigns.filter((item) => item.id !== campaign.id)
    const success = await persist([...others, campaign], `已保存“${campaign.name}”。`)
    if (success) setDraft(draftFromCampaign(campaign))
  }

  const handleDelete = async () => {
    if (!draft || !campaignsData) return
    if (!pendingDelete) { setPendingDelete(true); return }
    const others = campaignsData.campaigns.filter((item) => item.id !== draft.id)
    await persist(others, '已删除活动。', true)
    setPendingDelete(false)
  }

  if (!data) return null
  const savedCampaigns = campaignsData?.campaigns || []
  const draftIsNew = !!draft && !savedCampaigns.some((item) => item.id === draft.id)

  return (
    <div className="detail-page campaign-analysis-page">
      <div className="section-heading-row period-heading">
        <div>
          <h1>活动分析</h1>
          <p>
            活动前利润测算 · 日常价取商品清单快照 {listing?.latestExportDate || '加载中'}
            · 支持多层优惠叠加，利润按商家实收并扣除京挑客 CPS 佣金
          </p>
        </div>
      </div>

      <section className="detail-section campaign-picker-row">
        <div className="campaign-picker">
          <span>活动</span>
          <select
            value={draft?.id || ''}
            onChange={(event) => {
              const campaign = savedCampaigns.find((item) => item.id === event.target.value)
              selectCampaign(campaign || null)
            }}
          >
            <option value="">选择已保存活动…</option>
            {savedCampaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}{campaign.startDate ? `（${campaign.startDate} 起）` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="campaign-new-button"
            onClick={() => {
              selectCampaign(null)
              setDraft(emptyDraft())
            }}
          >
            <Plus aria-hidden="true" />新建活动
          </button>
          {draft && (
            <button
              type="button"
              className="campaign-new-button"
              title="以当前活动的商品池和优惠层创建副本"
              onClick={() => {
                const source = campaignFromDraft(draft)
                setDraft(draftFromCampaign(source, true))
                setMessage('已创建副本，调整后保存即为新活动。')
                setError('')
                setPendingDelete(false)
              }}
            >
              <Copy aria-hidden="true" />复制活动
            </button>
          )}
          {campaignsData === null && <span className="profit-missing">活动数据加载失败，请确认本地服务已重启。</span>}
        </div>
      </section>

      {draft && (
        <>
          <section className="detail-section">
            <h2>活动信息</h2>
            <div className="campaign-form-grid">
              <label>
                <span>活动名称 *</span>
                <input value={draft.name} placeholder="如：8月开学季大促" onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
              </label>
              <label>
                <span>开始日期</span>
                <input type="date" value={draft.startDate} onChange={(event) => setDraft({ ...draft, startDate: event.target.value })} />
              </label>
              <label>
                <span>结束日期</span>
                <input type="date" value={draft.endDate} onChange={(event) => setDraft({ ...draft, endDate: event.target.value })} />
              </label>
              <label>
                <span>默认京挑客 CPS 佣金率（%）</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={draft.cpsCommissionPercent}
                  placeholder="0"
                  aria-label="默认京挑客 CPS 佣金率"
                  className={cpsCommissionPercentValid ? '' : 'is-invalid'}
                  onChange={(event) => setDraft({ ...draft, cpsCommissionPercent: event.target.value })}
                />
              </label>
              <label className="campaign-note-field">
                <span>活动规则说明</span>
                <input value={draft.note} placeholder="如：报名截止7-25；PLUS会员券与满减可叠加" onChange={(event) => setDraft({ ...draft, note: event.target.value })} />
              </label>
            </div>
          </section>

          <section className="detail-section">
            <div className="section-heading-row">
              <div>
                <h2>优惠层</h2>
                <p>按顺序折上折叠加；每层可设承担比例，平台承担部分计入商家实收</p>
              </div>
              <button
                type="button"
                className="campaign-new-button"
                onClick={() => setDraft({ ...draft, layers: [...draft.layers, emptyLayer()] })}
              >
                <Plus aria-hidden="true" />添加优惠层
              </button>
            </div>
            {!draft.layers.length && <p className="formula-empty">尚未添加优惠层；不加层时按“活动底价”直接测算。</p>}
            {draft.layers.map((layer, index) => {
              const parsed = parsedLayers[index]
              return (
                <div key={layer.key} className="campaign-layer-row">
                  <span className="campaign-layer-index">{index + 1}</span>
                  <input
                    className="campaign-layer-name"
                    value={layer.name}
                    placeholder={`优惠层${index + 1}，如：五折日 / PLUS会员`}
                    onChange={(event) => updateLayer(layer.key, { name: event.target.value })}
                  />
                  <select
                    value={layer.method}
                    aria-label="促销方式"
                    onChange={(event) => updateLayer(layer.key, { method: event.target.value as CampaignLayerMethod })}
                  >
                    {(Object.keys(LAYER_METHOD_LABELS) as CampaignLayerMethod[]).map((method) => (
                      <option key={method} value={method}>{LAYER_METHOD_LABELS[method]}</option>
                    ))}
                  </select>
                  {layer.method === 'threshold' && (
                    <span className="campaign-layer-field">
                      每满
                      <input value={layer.threshold} placeholder="300" aria-label="满减门槛" onChange={(event) => updateLayer(layer.key, { threshold: event.target.value })} />
                      元
                    </span>
                  )}
                  <span className="campaign-layer-field">
                    {layer.method === 'amount' ? '降' : layer.method === 'threshold' ? '减' : ''}
                    <input
                      value={layer.value}
                      placeholder={layer.method === 'percent' ? '如 5＝五折' : layer.method === 'amount' ? '金额' : '20'}
                      aria-label="力度"
                      onChange={(event) => updateLayer(layer.key, { value: event.target.value })}
                    />
                    {layer.method === 'percent' ? '折' : '元'}
                  </span>
                  <span className="campaign-layer-field">
                    平台担
                    <input value={layer.platformShare} placeholder="0" aria-label="平台承担比例" onChange={(event) => updateLayer(layer.key, { platformShare: event.target.value })} />
                    %
                  </span>
                  <span className={`campaign-layer-summary${parsed.valid ? '' : ' profit-missing'}`}>{layerSummary(parsed)}</span>
                  <span className="campaign-layer-actions">
                    <button type="button" aria-label="上移" disabled={index === 0} onClick={() => moveLayer(layer.key, -1)}><ArrowUp aria-hidden="true" /></button>
                    <button type="button" aria-label="下移" disabled={index === draft.layers.length - 1} onClick={() => moveLayer(layer.key, 1)}><ArrowDown aria-hidden="true" /></button>
                    <button type="button" aria-label="删除优惠层" onClick={() => setDraft({ ...draft, layers: draft.layers.filter((item) => item.key !== layer.key) })}><Trash2 aria-hidden="true" /></button>
                  </span>
                </div>
              )
            })}
          </section>

          <section className="detail-section">
            <h2>批量添加SKU</h2>
            <div className="campaign-batch-row">
              <textarea
                value={batchText}
                placeholder={'一行一个京东SKU或商家SKU，例如：\n10154324408478\n11116313'}
                rows={4}
                onChange={(event) => setBatchText(event.target.value)}
              />
              <button type="button" onClick={applyBatchInput} disabled={!batchText.trim()}>解析并加入</button>
            </div>
            {batchReport && <p className="campaign-batch-report">{batchReport}</p>}
          </section>

          <section className="detail-section">
            <div className="section-heading-row">
              <div>
                <h2>单件利润测算</h2>
                <p>共 {integer(rows.length)} 个SKU · 活动底价留空＝日常价（先降价时手填，如148填138）· 生效优惠层 {integer(activeLayers.length)} 层 · 默认CPS {percent(cpsCommissionPercent)}，可逐SKU覆盖</p>
              </div>
              <span className="campaign-sort-hint">点击“预估利润／含返点利润／利润率”表头可排序</span>
            </div>
            <MetricStrip items={[
              { label: '活动SKU', value: integer(summary.total), context: '当前活动商品池' },
              { label: '可算利润', value: integer(summary.computable), context: '产品成本已覆盖' },
              { label: '亏损SKU', value: integer(summary.losing), context: '单件含返点利润 < 0' },
              { label: '默认京挑客 CPS', value: percent(cpsCommissionPercent), context: '单个SKU可另行覆盖' },
              {
                label: '平均利润率',
                value: summary.averageMargin === null ? '—' : percent(summary.averageMargin * 100),
                context: '可算SKU简单平均',
              },
            ]} />
            {!rows.length && <p className="formula-empty">尚未添加SKU；在上方批量粘贴后解析加入。</p>}
            {!!rows.length && (
              <div className="table-responsive">
                <table className="history-table campaign-table">
                  <thead>
                    <tr>
                      <th>序号</th>
                      <th>商品</th>
                      <th>日常价</th>
                      <th>活动底价</th>
                      <th>到手价</th>
                      <th>商家实收</th>
                      <th>产品成本</th>
                      <th>包装费</th>
                      <th>运费</th>
                      <th>赠品费</th>
                      <th>运费险</th>
                      <th>履约成本合计</th>
                      <th>扣点税点</th>
                      <th>CPS佣金率</th>
                      <th>CPS佣金</th>
                      {renderSortableHeader('profit', '单件预估利润')}
                      <th>单件返点</th>
                      {renderSortableHeader('profitWithRebate', '单件含返点利润')}
                      {renderSortableHeader('margin', '利润率')}
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, displayIndex) => (
                      <tr key={row.item.jdSkuId}>
                        <td className="sku-row-number">{displayIndex + 1}</td>
                        <td className="new-product-cell-wrap">
                          <div className="new-product-cell">
                            {row.product?.image
                              ? <img src={row.product.image} alt="" />
                              : <span className="new-product-image-fallback">无图</span>}
                            <span>
                              <Link to={`/product/sku/${row.item.jdSkuId}`} title={row.product?.name || `SKU ${row.item.jdSkuId}`}>
                                {row.product?.name || `SKU ${row.item.jdSkuId}`}
                              </Link>
                              <small>{row.item.jdSkuId}{row.product?.merchantSku ? ` · ${row.product.merchantSku}` : ''}</small>
                            </span>
                          </div>
                        </td>
                        <td>{row.jdPrice === null ? '—' : money(row.jdPrice)}</td>
                        <td className="campaign-price-cell">
                          <input
                            value={row.item.basePrice}
                            placeholder={row.jdPrice === null ? '手动填写' : row.jdPrice.toFixed(2)}
                            aria-label={`活动底价 ${row.item.jdSkuId}`}
                            className={row.manualValid ? '' : 'is-invalid'}
                            onChange={(event) => updateItemBasePrice(row.item.jdSkuId, event.target.value)}
                          />
                          {row.basePrice !== null && row.jdPrice !== null && row.basePrice < row.jdPrice
                            && <small>先降 {money(row.jdPrice - row.basePrice)}</small>}
                        </td>
                        <td className="campaign-cost-cell">
                          {row.finalPrice === null ? '—' : money(row.finalPrice)}
                          {row.priceRatio !== null && <small>{(row.priceRatio * 10).toFixed(2)}折</small>}
                        </td>
                        <td className="campaign-receive-cell" title={row.steps.join('；') || undefined}>
                          {row.merchantReceives === null ? '—' : money(row.merchantReceives)}
                          {row.subsidy > 0 && <small>含平台补贴 {money(row.subsidy)}</small>}
                        </td>
                        <td className="campaign-cost-cell">
                          {row.internalCost.productCost === null
                            ? <span className="profit-missing">缺失</span>
                            : money(row.internalCost.productCost)}
                          <small>{row.internalCost.isBrandSpecial ? '品牌特价' : row.internalCost.productCost === null ? '未关联' : '总代价'}</small>
                        </td>
                        <td>{money(row.cost.packagingCost)}</td>
                        <td className="campaign-cost-cell">
                          {money(row.cost.shippingFee)}
                          <small>{shippingProviderLabel(row.cost.shippingProvider)}</small>
                        </td>
                        <td className="campaign-cost-cell">
                          {money(row.cost.giftCost)}
                          {row.cost.giftCost === 0 && <small>{row.isAccessory ? '配件不计' : row.internalCost.isBrandSpecial ? '特价不计' : ''}</small>}
                        </td>
                        <td>{money(row.cost.shippingInsuranceFee)}</td>
                        <td title="产品成本＋包装＋运费＋赠品＋运费险">
                          <strong>{row.unitCost === null ? '—' : money(row.unitCost)}</strong>
                        </td>
                        <td className="campaign-cost-cell">
                          {row.merchantReceives === null ? '—' : money(row.merchantReceives * row.rateSum / 100)}
                          <small>{percent(row.rateSum)} × 实收</small>
                        </td>
                        <td className="campaign-price-cell campaign-rate-cell">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={row.item.cpsCommissionPercent}
                            placeholder={String(cpsCommissionPercent)}
                            aria-label={`CPS佣金率 ${row.item.jdSkuId}`}
                            className={row.itemCpsCommissionPercentValid ? '' : 'is-invalid'}
                            onChange={(event) => updateItemCpsCommissionPercent(row.item.jdSkuId, event.target.value)}
                          />
                          <small>{row.item.cpsCommissionPercent.trim() === '' ? '继承活动默认' : 'SKU单独设置'}</small>
                        </td>
                        <td className="campaign-cost-cell">
                          {row.cpsCommission === null ? '—' : money(row.cpsCommission)}
                          <small>{percent(row.effectiveCpsCommissionPercent)} × 到手价</small>
                        </td>
                        <td
                          className={row.profit === null ? 'profit-missing' : row.profit >= 0 ? 'profit-positive' : 'profit-negative'}
                          title="商家实收－履约成本－扣点税点－CPS佣金"
                        >
                          {row.profit === null ? '—' : money(row.profit)}
                        </td>
                        <td>{row.rebateUnit === null ? (row.rebateRate === null ? '不计' : '—') : money(row.rebateUnit)}</td>
                        <td className={row.profitWithRebate === null ? 'profit-missing' : row.profitWithRebate >= 0 ? 'profit-positive' : 'profit-negative'}>
                          <strong>{row.profitWithRebate === null ? '—' : money(row.profitWithRebate)}</strong>
                        </td>
                        <td className={row.margin === null ? '' : row.margin >= 0 ? 'profit-positive' : 'profit-negative'}>
                          {row.margin === null ? '—' : percent(row.margin * 100)}
                        </td>
                        <td>
                          <button type="button" className="campaign-remove-button" aria-label={`移除 ${row.item.jdSkuId}`} onClick={() => removeItem(row.item.jdSkuId)}>
                            <Trash2 aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="detail-section">
            <div className="section-heading-row">
              <div>
                <h2>活动复盘</h2>
                {reviewWindow
                  ? <p>实际数据 {reviewWindow.start} 至 {reviewWindow.end}（覆盖 {reviewWindow.dates.length}/{reviewWindow.requestedDays} 天）· 仅列出动销SKU · 实际与预期利润均已扣CPS佣金</p>
                  : draft.startDate
                    ? <p>活动期（{draft.startDate} 起）尚无销售数据；活动开始并完成每日更新后自动生成复盘对比。</p>
                    : <p>填写活动开始日期后，销售数据覆盖到活动期间即自动生成复盘对比。</p>}
              </div>
            </div>
            {review && !!review.items.length && (
              <>
                <MetricStrip items={[
                  { label: '实际成交金额', value: money(review.totalGmv), context: `动销 ${integer(review.items.length)} 个SKU` },
                  { label: '实际成交件数', value: integer(review.totalItems), context: '活动窗口分天累加' },
                  { label: '实际CPS佣金', value: money(review.totalCpsCommission), context: '按各SKU设置×实际成交金额' },
                  {
                    label: '实际含返点利润',
                    value: review.actualProfit === null ? '—' : money(review.actualProfit),
                    context: `成本覆盖 ${integer(review.coveredCount)}/${integer(review.items.length)} 个动销SKU`,
                  },
                  {
                    label: '预期利润（按测算）',
                    value: review.expectedProfit === null ? '—' : money(review.expectedProfit),
                    context: '测算单件利润 × 实际件数',
                  },
                  {
                    label: '实际 − 预期',
                    value: review.actualProfit !== null && review.expectedProfit !== null
                      ? money(review.actualProfit - review.expectedProfit)
                      : '—',
                    context: '正值代表好于测算',
                  },
                ]} />
                <div className="table-responsive">
                  <table className="history-table campaign-table campaign-review-table">
                    <thead>
                      <tr>
                        <th>序号</th>
                        <th>商品</th>
                        <th>实际成交金额</th>
                        <th>成交件数</th>
                        <th>CPS佣金率</th>
                        <th>实际CPS佣金</th>
                        <th>实际含返点利润</th>
                        <th>测算单件利润</th>
                        <th>预期利润</th>
                        <th>实际−预期</th>
                      </tr>
                    </thead>
                    <tbody>
                      {review.items.map((row, index) => (
                        <tr key={row.jdSkuId}>
                          <td className="sku-row-number">{index + 1}</td>
                          <td className="new-product-cell-wrap">
                            <div className="new-product-cell">
                              {row.product.image
                                ? <img src={row.product.image} alt="" />
                                : <span className="new-product-image-fallback">无图</span>}
                              <span>
                                <Link to={`/product/sku/${row.jdSkuId}`} title={row.product.name}>{row.product.name}</Link>
                                <small>{row.jdSkuId}{row.product.merchantSku ? ` · ${row.product.merchantSku}` : ''}</small>
                              </span>
                            </div>
                          </td>
                          <td>{money(row.actual.gmv)}</td>
                          <td>{integer(row.actual.soldItemCount)}</td>
                          <td>{percent(row.itemCpsCommissionPercent)}</td>
                          <td>{money(row.actualCpsCommission)}</td>
                          <td className={row.actualProfit === null ? 'profit-missing' : row.actualProfit >= 0 ? 'profit-positive' : 'profit-negative'}>
                            {row.actualProfit === null ? '—' : money(row.actualProfit)}
                          </td>
                          <td>{row.expectedUnit === null ? '—' : money(row.expectedUnit)}</td>
                          <td>{row.expected === null ? '—' : money(row.expected)}</td>
                          <td className={row.diff === null ? '' : row.diff >= 0 ? 'profit-positive' : 'profit-negative'}>
                            {row.diff === null ? '—' : money(row.diff)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {reviewWindow && review && !review.items.length && (
              <p className="formula-empty">活动窗口内商品池暂无成交记录。</p>
            )}
          </section>

          <section className="detail-section campaign-actions-row">
            <button type="button" className="campaign-save-button" onClick={handleSave} disabled={saving}>
              <Save aria-hidden="true" />{saving ? '正在保存…' : draftIsNew ? '保存新活动' : '保存活动'}
            </button>
            {!draftIsNew && (
              <button type="button" className="campaign-delete-button" onClick={handleDelete} disabled={saving}>
                <Trash2 aria-hidden="true" />{pendingDelete ? '再次点击确认删除' : '删除活动'}
              </button>
            )}
            {message && <span className="form-success">{message}</span>}
            {error && <span className="form-error">{error}</span>}
          </section>
        </>
      )}

      {!draft && (
        <p className="formula-empty campaign-empty-hint">
          选择一个已保存活动，或点击“新建活动”开始测算；也可以选中历史活动后点“复制活动”，沿用商品池和优惠层，只改时间、增减SKU或叠加新优惠层。
        </p>
      )}

      <p className="methodology-note">
        价格链 ＝ 日常价 → 活动底价（先降价，商家承担）→ 各优惠层按顺序折上折；每满减按“减额÷门槛”折算为让利率（凑满门槛假设）。
        商家实收 ＝ 最终到手价 ＋ Σ(每层让利 × 该层平台承担比例)；CPS佣金 ＝ 最终到手价 × 京挑客佣金率（SKU单独设置优先，留空继承活动默认，平台补贴不计佣）；单件预估利润 ＝ 实收 − (产品成本＋包装＋运费＋赠品＋运费险) − 实收 × (平台扣点＋税点) − CPS佣金；
        含返点利润 ＝ 预估利润 ＋ 产品成本 × 返点率；利润率 ＝ 含返点利润 ÷ 实收。成本口径与全站一致；活动保存于本项目 config/campaign-analysis.json，只写本地。
      </p>
    </div>
  )
}
